import React from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';

export interface AppLogoProps {
  variant?: 'icon' | 'with-text';
  color?: 'primary' | 'secondary' | 'accent' | 'white';
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  width?: number | string;
  height?: number | string;
  alt?: string;
  title?: string;
  className?: string;
  sx?: SxProps<Theme>;
  onClick?: () => void;
}

const LOGO_FILES = {
  icon: {
    primary: 'familycast_logo_500x500',
    secondary: 'familycast_logo_500x500',
    accent: 'familycast_logo_500x500',
    white: 'familycast_logo_500x500',
  },
  'with-text': {
    primary: 'familycast_logo_text_500x500',
    secondary: 'familycast_logo_text_500x500',
    accent: 'familycast_logo_text_500x500',
    white: 'familycast_logo_text_500x500',
  },
};

const LOGO_SIZES = {
  small: { width: 32, height: 32 },
  medium: { width: 64, height: 64 },
  large: { width: 128, height: 128 },
  xlarge: { width: 256, height: 256 },
};

export const AppLogo: React.FC<AppLogoProps> = ({
  variant = 'icon',
  color = 'primary',
  size = 'medium',
  width,
  height,
  alt,
  title,
  className,
  sx,
  onClick,
}) => {
  const dimensions = width || height
    ? { width, height: height || width }
    : LOGO_SIZES[size];

  const filename = LOGO_FILES[variant][color];
  const imgSrc = `/images/${filename}.webp`;
  const fallbackSrc = `/images/${filename}.png`;

  return (
    <Box
      component="img"
      src={imgSrc}
      onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        e.currentTarget.onerror = null; // Prevent infinite loop
        e.currentTarget.src = fallbackSrc;
      }}
      alt={alt || 'FamilyCast Logo'}
      title={title}
      className={className}
      onClick={onClick}
      sx={{
        width: dimensions.width,
        height: dimensions.height,
        objectFit: 'contain',
        cursor: onClick ? 'pointer' : 'default',
        userSelect: 'none',
        ...sx,
      }}
    />
  );
};

export default AppLogo;
